// /blog 編集 modal(2026-07-06 追加 / Stage Editor-2 で toolbar 共通化)
//
// UI 仕様(Claudia テキスト spec):
// - 大きな modal(80vw / max-width 1100px / max-height 85vh)
// - 白背景 + 12-16px 圆角 + 浅阴影
// - 2 栏レイアウト:左 68% 主編集 / 右 260-300px 投稿情報
// - 左:タイトル input(必須 / 200 字以内 / 文字数表示) + 本文 editor
//       (BlogDraftEditor が共通 toolbar + WYSIWYG / Code / Preview を描画)
// - 右:ステータス badge / WP ID / 作成日時 / 更新日時 / link / WordPressで開く 按钮 / info box
// - 底部:キャンセル / 変更を保存
// - 未保存変更時 × / キャンセル 触发 confirm
// - 保存失敗時:赤 box「保存に失敗しました: {msg}」+ error_code
// - 成功時:緑 box「記事を更新しました」+ reloadPosts
// - 禁:status 変更 / dangerouslySetInnerHTML / Gutenberg / 画像 / 分类
//
// Toolbar:
// - toolbar 本体は所有しない。BlogDraftEditor が内部で BlogDraftEditorToolbar
//   (icon + dropdown paragraph/heading) を描画する。
// - AI Blog ページも同じ toolbar を経由する(`/admin/blog` modal と
//   `/admin/ai-blog` Page が同一実装)。

import { useEffect, useState } from "react";
import {
  X,
  Save,
  ExternalLink,
  AlertCircle,
  CheckCircle2,
  Info,
} from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";
import { ApiError } from "../../api/client";
import { blogApi, type BlogPostDetail } from "../../api/blog";
import { BlogDraftEditor } from "../../components/blog/BlogDraftEditor";

const TITLE_MAX = 200;
const CONTENT_MAX = 50000;

/**
 * 2026-09-16 方針 B:WP から取得した HTML を「img の width を
 * inline style に統一」した形へマイグレートする。
 *
 * 背景:
 * - WordPress / 一部旧 backend / 手書き HTML で `<img width="500">`
 *   という HTML attribute 形式の width が残っている。
 * - HTML 仕様上 width attribute は integer pixel のみ(valid)で、
 *   それ以外は invalid → ブラウザ + sanitizer で attribute が無視/剥奪される。
 * - 一方 inline `style="width:500px;"` はブラウザ + 全 sanitizer が
 *   正しく扱う(パーセントや em 等も OK)。
 * - ツールバー dropdown は inline style しか読まない(方針 B)。
 *
 * この関数は:
 *   1) `<img width="N">` を見つけて style へ移動
 *   2) 既に inline style があれば display / margin 等を保持しつつ
 *      width を append する(display / float の重複は後に書いた方を優先)
 *
 * 注意:
 * - この変換は **読み込み時にのみ** 走る。保存時に再度走らせない限り、
 *   他の edit 経路には伝播しない(editor 内部表現は正)。
 * - editor の renderHTML は既に style に統一して書くので、保存時には
 *   このマイグレーション済み形 + 正しい style が出力される。
 */
function normalizeImgWidthToInlineStyle(html: string): string {
  if (!html) return html;
  // パターン: <img ... width="500" ...> を含む全 img タグ
  return html.replace(
    /<img\b([^>]*?)\bwidth="(\d+)"([^>]*?)>/gi,
    (_match, before: string, w: string, after: string) => {
      const beforeAttrs = `${before} ${after}`.trim();
      const widthDecl = `width: ${w}px`;
      // 既に style="..." が書いてある場合は width を append (display / margin 等は保持)
      const styleMatch = beforeAttrs.match(/\bstyle="([^"]*)"/i);
      if (styleMatch) {
        const existing = styleMatch[1];
        // 既存 style に width が含まれていたら置き換え、無ければ append
        const newStyle = existing
          .replace(/(?:^|[;\s])width\s*:\s*[^;]*(?:;|$)/i, "")
          .trim()
          .replace(/;\s*$/, "");
        const joined = newStyle
          ? `${newStyle}; ${widthDecl};`
          : `${widthDecl};`;
        const replaced = beforeAttrs.replace(
          /\bstyle="[^"]*"/i,
          `style="${joined}"`,
        );
        // width attribute を除去
        return `<img ${replaced.replace(/\s*width="\d+"/i, "")}>`;
      }
      // style が無いなら新規追加(width は後ろ、align 用 style が前)
      const withoutWidth = beforeAttrs.replace(/\s*width="\d+"/i, "");
      // align: 旧 data-align / 旧 class はこの関数では触らない(inline style のみ)
      return `<img ${withoutWidth} style="${widthDecl};">`;
    },
  );
}

function statusBadgeColor(
  status: string
): "green" | "gray" | "yellow" | "red" {
  if (status === "publish") return "green";
  if (status === "draft") return "gray";
  if (status === "future") return "yellow";
  if (status === "private") return "red";
  return "gray";
}

function statusLabel(status: string): string {
  const map: Record<string, string> = {
    publish: "公開",
    draft: "下書き",
    future: "予約",
    private: "非公開",
    pending: "レビュー待ち",
  };
  return map[status] ?? status;
}

// JST 表示(yyyy-mm-dd hh:mm)— blog の /api/blog/posts と同じ形式に揃える
function formatJst(iso: string): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    // Asia/Tokyo で yyyy-mm-dd hh:mm
    const fmt = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Tokyo",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    const parts = fmt.formatToParts(d).reduce<Record<string, string>>((acc, p) => {
      acc[p.type] = p.value;
      return acc;
    }, {});
    return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}`;
  } catch {
    return iso;
  }
}

// ツールバー ボタン:UI のみで実際の編集機能はなし(将来拡張)。
// (旧 `ToolbarIconButton` 関数を削除:
//  共通 BlogDraftEditorToolbar を BlogDraftEditor が内部で描画するため、
//  本 modal は toolbar を所有しない(isAiBlog / pageType 等の分岐なし)。)



interface BlogEditModalProps {
  postId: number | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void; // 保存成功 / 关闭后由父组件 reload 列表
}

export const BlogEditModal = ({ postId, open, onClose, onSaved }: BlogEditModalProps) => {
  // 入力 state
  const [title, setTitle] = useState<string>("");
  const [content, setContent] = useState<string>("");

  // 詳細情報(post id / status / link / created_at / updated_at)
  const [detail, setDetail] = useState<BlogPostDetail | null>(null);

  // ローディング / エラー
  const [loading, setLoading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadErrorCode, setLoadErrorCode] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveErrorCode, setSaveErrorCode] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);

  // 未保存変更検出
  const [originalTitle, setOriginalTitle] = useState<string>("");
  const [originalContent, setOriginalContent] = useState<string>("");

  // 打开时:GET 取得詳細
  useEffect(() => {
    if (!open || postId == null) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setLoadErrorCode(null);
    setSaveError(null);
    setSaveErrorCode(null);
    setSaveSuccess(false);
    (async () => {
      try {
        const d = await blogApi.getPostDetail(postId);
        if (cancelled) return;
        // 2026-09-16 方針 B: WP 上の旧 `<img width="N">` を inline style に
        // マイグレートしてから editor に渡す(parseWidthAttr が inline style を
        // 読む唯一の正準ルートなので)。
        const normalizedContent = normalizeImgWidthToInlineStyle(d.content);
        setDetail(d);
        setTitle(d.title);
        setContent(normalizedContent);
        setOriginalTitle(d.title);
        setOriginalContent(normalizedContent);
      } catch (e) {
        if (cancelled) return;
        if (e instanceof ApiError) {
          setLoadError(e.message);
          setLoadErrorCode(e.code);
        } else {
          setLoadError("読み込みに失敗しました。");
          setLoadErrorCode("UNKNOWN");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, postId]);

  // ESC キー で close
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleRequestClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, title, content, originalTitle, originalContent]);

  const isDirty = title !== originalTitle || content !== originalContent;
  const canSave =
    !saving &&
    !loading &&
    title.trim().length > 0 &&
    title.length <= TITLE_MAX &&
    content.length >= 1 &&
    content.length <= CONTENT_MAX;

  function handleRequestClose() {
    if (isDirty) {
      const ok = window.confirm(
        "保存していない変更があります。閉じてもよろしいですか?"
      );
      if (!ok) return;
    }
    onClose();
  }

  async function handleSave() {
    if (postId == null) return;
    if (!canSave) return;
    setSaving(true);
    setSaveError(null);
    setSaveErrorCode(null);
    setSaveSuccess(false);
    try {
      const updated = await blogApi.updatePost(postId, {
        title: title.trim(),
        content,
      });
      // 成功:新しい original 値更新 + 詳細更新
      setDetail(updated);
      setOriginalTitle(updated.title);
      setOriginalContent(updated.content);
      setTitle(updated.title);
      setContent(updated.content);
      setSaveSuccess(true);
      // 父の list reload(本文 title / updated_at 更新)
      onSaved();
    } catch (e) {
      if (e instanceof ApiError) {
        setSaveError(e.message);
        setSaveErrorCode(e.code);
      } else if (e instanceof Error) {
        setSaveError("通信エラーが発生しました。しばらく経ってから再度お試しください。");
        setSaveErrorCode("NETWORK_ERROR");
      } else {
        setSaveError("保存に失敗しました。");
        setSaveErrorCode("UNKNOWN");
      }
    } finally {
      setSaving(false);
    }
  }

  // 表示用文字数カウンタは BlogDraftEditor 側で描画するため、ここでは不要。
  // (Stage Editor-1 で BlogDraftEditor に統一)

  if (!open) return null;

  return (
    <div
      className="blog-edit-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="記事を編集"
      onClick={handleRequestClose}
    >
      <div
        className="blog-edit-modal-container"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ヘッダー */}
        <div className="blog-edit-modal-header">
          <div className="blog-edit-modal-title">記事を編集</div>
          <button
            type="button"
            className="blog-edit-modal-close"
            onClick={handleRequestClose}
            aria-label="閉じる"
          >
            <X size={18} />
          </button>
        </div>

        {/* ロード中 */}
        {loading && (
          <div className="blog-edit-modal-loading">読み込み中...</div>
        )}

        {/* ロード失敗 */}
        {!loading && loadError && (
          <div className="blog-edit-modal-load-error">
            <div className="ai-error-box" role="alert">
              <AlertCircle size={14} />
              <div className="ai-error-text">
                <div className="ai-error-message">{loadError}</div>
                {loadErrorCode && (
                  <div className="ai-error-code">error_code: {loadErrorCode}</div>
                )}
              </div>
            </div>
            <div className="row gap-8 mt-12">
              <Button onClick={onClose} variant="secondary">
                閉じる
              </Button>
            </div>
          </div>
        )}

        {/* ロード成功后本文 */}
        {!loading && !loadError && detail && (
          <>
            {/* 2 カラム本体 */}
            <div className="blog-edit-modal-body">
              {/* 左:主編集 */}
              <div className="blog-edit-modal-main">
                {/* Stage Editor-2:title + body は BlogDraftEditor に統一。
                    toolbar (BlogDraftEditorToolbar) は BlogDraftEditor が
                    内部で描画するため、本 modal では渡さない。
                    親 state(title / content)は handleSave / isDirty / canSave が
                    使うので保持し、onChange で同期する。 */}
                <BlogDraftEditor
                  value={{ title, body: content }}
                  onChange={(next) => {
                    setTitle(next.title);
                    setContent(next.body);
                  }}
                  mode="edit"
                  readOnly={saving}
                  maxTitleLength={TITLE_MAX}
                  maxBodyLength={CONTENT_MAX}
                />
                {/* 注:toolbar は BlogDraftEditor が内部で共通 BlogDraftEditorToolbar
                    を通じて描画する(本 modal では重複描画しない)。
                    Blog Draft タブと AI Blog タブは両者ともこの同じ toolbar を経由する。 */}
              </div>

              {/* 縦区切り線 */}
              <div className="blog-edit-modal-divider" />

              {/* 右:投稿情報 */}
              <div className="blog-edit-modal-sidebar">
                <div className="blog-edit-sidebar-title">投稿情報</div>

                <div className="blog-edit-sidebar-row">
                  <div className="blog-edit-sidebar-label">ステータス</div>
                  <div>
                    <Badge color={statusBadgeColor(detail.status)} dot>
                      {statusLabel(detail.status)}
                    </Badge>
                  </div>
                </div>

                <div className="blog-edit-sidebar-row">
                  <div className="blog-edit-sidebar-label">WordPress ID</div>
                  <div className="blog-edit-sidebar-value">{detail.id}</div>
                </div>

                <div className="blog-edit-sidebar-row">
                  <div className="blog-edit-sidebar-label">作成日時</div>
                  <div className="blog-edit-sidebar-value">
                    {formatJst(detail.created_at)}
                  </div>
                </div>

                <div className="blog-edit-sidebar-row">
                  <div className="blog-edit-sidebar-label">更新日時</div>
                  <div className="blog-edit-sidebar-value">
                    {formatJst(detail.updated_at)}
                  </div>
                </div>

                <div className="blog-edit-sidebar-row">
                  <div className="blog-edit-sidebar-label">リンク</div>
                  {detail.link ? (
                    <a
                      href={detail.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="blog-edit-sidebar-link"
                      title={detail.link}
                    >
                      {detail.link}
                    </a>
                  ) : (
                    <div className="blog-edit-sidebar-value">—</div>
                  )}
                </div>

                {detail.link && (
                  <a
                    href={detail.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="blog-edit-sidebar-wp-btn"
                  >
                    <ExternalLink size={12} />
                    <span>WordPressで開く</span>
                  </a>
                )}

                <div className="blog-edit-sidebar-info">
                  <Info size={14} className="blog-edit-sidebar-info-icon" />
                  <span>
                    ステータスの変更は一覧の「公開」「下書き」ボタンから行ってください。
                  </span>
                </div>
              </div>
            </div>

            {/* 成功 / 失敗メッセージ */}
            {saveSuccess && (
              <div className="blog-edit-modal-success" role="status">
                <CheckCircle2 size={14} />
                <span>記事を更新しました</span>
              </div>
            )}
            {saveError && (
              <div className="ai-error-box mt-12" role="alert">
                <AlertCircle size={14} />
                <div className="ai-error-text">
                  <div className="ai-error-message">
                    保存に失敗しました: {saveError}
                  </div>
                  {saveErrorCode && (
                    <div className="ai-error-code">error_code: {saveErrorCode}</div>
                  )}
                </div>
              </div>
            )}

            {/* フッター */}
            <div className="blog-edit-modal-footer">
              <Button onClick={handleRequestClose} variant="ghost" disabled={saving}>
                キャンセル
              </Button>
              <Button
                onClick={handleSave}
                disabled={!canSave}
                iconLeft={<Save size={14} />}
              >
                {saving ? "保存中..." : "変更を保存"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
